<template>
  <el-dialog
    :title="$t('admin.MachineGroupAdvertConfig.title')"
    :visible.sync="dialogVisible"
    width="1200px"
    top="5vh"
    :before-close="handleClose"
    :close-on-click-modal="false"
    custom-class="machine-group-advert-dialog"
  >
    <div v-loading="loading" class="advert-group-config">
      <div class="current-group-section">
        <div class="current-info">
          <span class="label">{{ $t("admin.MachineGroupAdvertConfig.currentGroup") }}</span>
          <el-tag v-if="selectedAdvertGroup" type="primary" size="medium">
            {{ selectedAdvertGroup.name }}
            <span
              v-if="!currentAdvertGroup || selectedAdvertGroup.id !== currentAdvertGroup.id"
              style="margin-left: 5px; color: #e6a23c"
            >
              ({{ $t("admin.MachineGroupAdvertConfig.unsaved") }})
            </span>
          </el-tag>
          <el-tag v-else-if="currentAdvertGroup" type="success" size="medium">
            {{ currentAdvertGroup.name }}
          </el-tag>
          <span v-else style="color: #909399">{{ $t("admin.MachineGroupAdvertConfig.noConfig") }}</span>
        </div>
        <div class="action-buttons">
          <el-button size="small" icon="el-icon-edit" @click="showGroupSelector = true">
            {{ $t("admin.MachineGroupAdvertConfig.selectGroup") }}
          </el-button>
        </div>
      </div>
      <div>
        <el-empty
          v-if="!selectedAdvertGroup && !currentAdvertGroup"
          :description="$t('admin.MachineGroupAdvertConfig.pleaseSelect')"
        ></el-empty>
        <advert-slot-tabs
          v-else
          :screen-saver-adverts="screenSaverAdverts"
          :top-banner-adverts="topBannerAdverts"
          :external-screen-adverts="externalScreenAdverts"
          :active-tab.sync="activeTab"
          :editable="false"
        />
      </div>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t("common.cancel") }}</el-button>
      <el-button type="primary" :loading="saving" :disabled="!selectedAdvertGroup" @click="handleSave">
        {{ $t("common.definite") }}
      </el-button>
    </span>

    <el-dialog
      :title="$t('admin.MachineGroupAdvertConfig.selectGroup')"
      :visible.sync="showGroupSelector"
      width="750px"
      append-to-body
    >
      <div v-loading="groupListLoading">
        <el-form :inline="true" size="small" style="margin-bottom: 15px">
          <el-form-item :label="$t('admin.MachineGroupAdvertConfig.groupName')">
            <el-input
              v-model="searchKeyword"
              :placeholder="$t('admin.MachineGroupAdvertConfig.searchPlaceholder')"
              clearable
              style="width: 200px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">{{ $t("common.search") }}</el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">{{ $t("common.reset") }}</el-button>
          </el-form-item>
        </el-form>

        <el-table :data="advertGroupList" style="width: 100%" max-height="400">
          <el-table-column type="index" :label="$t('common.index')" width="60" align="center"></el-table-column>
          <el-table-column
            prop="name"
            :label="$t('admin.MachineGroupAdvertConfig.groupName')"
            align="center"
          ></el-table-column>
          <el-table-column :label="$t('common.operate')" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="selectedAdvertGroup && selectedAdvertGroup.id === scope.row.id"
                type="success"
                size="mini"
                disabled
              >
                {{ $t("common.selected") }}
              </el-button>
              <el-button v-else type="primary" size="mini" @click="handleSelectGroup(scope.row)">
                {{ $t("common.select") }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <el-pagination
          v-if="page.total > 0"
          background
          layout="prev, pager, next"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :total="page.total"
          @current-change="handlePageChange"
          style="margin-top: 20px; text-align: right"
        >
        </el-pagination>
      </div>
    </el-dialog>
  </el-dialog>
</template>

<script>
  import AdvertSlotTabs from "@/components/advert-config/AdvertSlotTabs.vue";

  export default {
    name: "MachineGroupAdvertConfig",
    components: {
      AdvertSlotTabs
    },
    props: {
      visible: {
        type: Boolean,
        default: false
      },
      machineGroupId: {
        type: String,
        default: ""
      },
      machineGroupName: {
        type: String,
        default: ""
      }
    },
    data() {
      return {
        dialogVisible: false,
        loading: false,
        saving: false,
        groupListLoading: false,
        showGroupSelector: false,
        searchKeyword: "",
        currentAdvertGroup: null,
        selectedAdvertGroup: null,
        advertGroupList: [],
        activeTab: "screenSaver",
        screenSaverAdverts: [],
        topBannerAdverts: [],
        externalScreenAdverts: [],
        page: {
          currentPage: 1,
          pageSize: 10,
          total: 0
        }
      };
    },
    watch: {
      visible(val) {
        this.dialogVisible = val;
        if (val) {
          this.init();
        }
      }
    },
    methods: {
      async init() {
        this.searchKeyword = "";
        this.currentAdvertGroup = null;
        this.selectedAdvertGroup = null;
        this.screenSaverAdverts = [];
        this.topBannerAdverts = [];
        this.externalScreenAdverts = [];
        this.page.currentPage = 1;
        this.activeTab = "screenSaver";
        await this.loadCurrentAdvertGroup();
        await this.loadAdvertGroupList();
      },

      async loadCurrentAdvertGroup() {
        if (!this.machineGroupId) {
          return;
        }

        this.loading = true;
        try {
          const res = await this.$api.request(this.$url.UserMachineGroup.getAdTeamById, this.$method.get, {
            teamId: this.machineGroupId
          });

          if (res.code === 0 && res.data && Array.isArray(res.data) && res.data.length > 0) {
            this.currentAdvertGroup = res.data[0];
            this.selectedAdvertGroup = res.data[0];
            await this.loadAdvertGroupAdverts(this.currentAdvertGroup.id);
          }
        } catch (error) {
          console.error("Load current advert group error:", error);
        } finally {
          this.loading = false;
        }
      },

      async loadAdvertGroupAdverts(advertGroupId) {
        if (!advertGroupId) return;

        try {
          const res = await this.$api.request(this.$url.AdvertGroup.listByAdTeamId, this.$method.get, {
            id: advertGroupId
          });

          if (res.code === 0 && res.data && Array.isArray(res.data)) {
            this.screenSaverAdverts = [];
            this.topBannerAdverts = [];
            this.externalScreenAdverts = [];

            res.data.forEach(advert => {
              if (advert.display === 1 || advert.display === "1") {
                this.screenSaverAdverts.push(advert);
              } else if (advert.display === 2 || advert.display === "2") {
                this.topBannerAdverts.push(advert);
              } else if (advert.display === 3 || advert.display === "3") {
                this.externalScreenAdverts.push(advert);
              }
            });

            this.screenSaverAdverts.sort((a, b) => (a.sort || 0) - (b.sort || 0));
            this.topBannerAdverts.sort((a, b) => (a.sort || 0) - (b.sort || 0));
            this.externalScreenAdverts.sort((a, b) => (a.sort || 0) - (b.sort || 0));
          }
        } catch (error) {
          console.error("Load advert group adverts error:", error);
        }
      },

      async loadAdvertGroupList() {
        this.groupListLoading = true;
        try {
          const params = {
            pageIndex: this.page.currentPage,
            pageSize: this.page.pageSize
          };

          if (this.searchKeyword) {
            params.name = this.searchKeyword;
          }

          const res = await this.$api.request(this.$url.AdvertGroup.page, this.$method.get, params);

          if (res.code === 0 && res.data) {
            this.advertGroupList = res.data.records || [];
            this.page.total = parseInt(res.data.total) || 0;
          }
        } catch (error) {
          console.error("Load advert group list error:", error);
          this.$message.error(this.$t("common.loadFailed"));
        } finally {
          this.groupListLoading = false;
        }
      },

      handleSearch() {
        this.page.currentPage = 1;
        this.loadAdvertGroupList();
      },

      handleReset() {
        this.searchKeyword = "";
        this.page.currentPage = 1;
        this.loadAdvertGroupList();
      },

      handlePageChange(page) {
        this.page.currentPage = page;
        this.loadAdvertGroupList();
      },

      async handleSelectGroup(row) {
        if (!row) return;

        this.selectedAdvertGroup = { ...row };
        this.showGroupSelector = false;
        await this.loadAdvertGroupAdverts(row.id);
      },

      async handleSave() {
        if (!this.selectedAdvertGroup) {
          this.$message.warning(this.$t("admin.MachineGroupAdvertConfig.pleaseSelect"));
          return;
        }

        if (this.currentAdvertGroup && this.selectedAdvertGroup.id === this.currentAdvertGroup.id) {
          this.$message.info(this.$t("admin.MachineGroupAdvertConfig.noChange"));
          return;
        }

        this.saving = true;
        try {
          const data = {
            teamId: this.machineGroupId,
            adTeamId: this.selectedAdvertGroup.id
          };

          const res = await this.$api.request(this.$url.UserMachine.configAdTeam, this.$method.put, data);

          if (res.code === 0) {
            this.$message.success(this.$t("common.saveSuccess"));
            this.currentAdvertGroup = { ...this.selectedAdvertGroup };
            this.$emit("success");
            this.handleClose();
          } else {
            this.$message.error(res.msg || this.$t("common.saveFailed"));
          }
        } catch (error) {
          console.error("Save advert config error:", error);
          this.$message.error(this.$t("common.saveFailed"));
        } finally {
          this.saving = false;
        }
      },

      handleClose() {
        this.$emit("update:visible", false);
        this.showGroupSelector = false;
      }
    }
  };
</script>

<style lang="scss" scoped>
  .advert-group-config {
    min-height: 500px;
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .current-group-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background: #f5f7fa;
    border-radius: 4px;
    border: 1px solid #dcdfe6;

    .current-info {
      display: flex;
      align-items: center;
      gap: 10px;

      .label {
        font-weight: 500;
        color: #606266;
      }
    }

    .action-buttons {
      display: flex;
      gap: 10px;
    }
  }

  ::v-deep .machine-group-advert-dialog {
    .el-dialog__body {
      max-height: 70vh;
      overflow-y: auto;
    }
  }
  ::v-deep .el-empty__image {
    width: 120px;
    height: 120px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }
</style>
